/* eslint-disable  @typescript-eslint/no-non-null-assertion */
import classnames from 'classnames';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { Carousel } from '@/core/tamagoshi/Carousel/Carousel';
import type { Merchandising } from '@/core/tamagoshi/ProductCard/interfaces';
import type { ProductCardProps } from '@/core/tamagoshi/ProductCard/ProductCard';
import { useDynamicReducer } from '@/domains/core/applicationState/hooks/useDynamicReducer';
import { isB2B } from '@/domains/core/settings/utils';
import { Direction } from '@/domains/core/tamagoshi/Carousel/components/Control/Control';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { AlgoliaProductCard } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/AlgoliaProductCard';
import type { ProductCardClickTracking } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/interfaces/clickTracking';
import { fingerprintReducer } from '@/domains/productDiscovery/fingerprint/fingerprint.reducer';
import { useBrandsFilter } from '@/domains/productDiscovery/Recommendation/hooks/useBrandsFilter/useBrandsFilter';
import { useCategoriesFilter } from '@/domains/productDiscovery/Recommendation/hooks/useCategoriesFilter/useCategoriesFilter';
import { useRecommendationPlaylistTracking } from '@/domains/productDiscovery/Recommendation/hooks/useRecommendationPlaylistTracking';
import type { RecommendationPlaylist } from '@/domains/productDiscovery/Recommendation/interfaces/productRecommendation';
import { usePlaylistReconciliation } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/usePlaylistReconciliation';
import { getScrollByFromWidth } from '@/domains/retention/Homepage/b2c/utils/getScrollByFromWidth';
import type { AddToCartPayload } from '@/domains/shoppingCart/AddToCart/AddToCart.actions';
import { getMerchandisingValue } from '@/productDiscovery/Listings/utils/productCard/merchandising';
import type { Product as RecoProductWithSponsoredData } from '@/productDiscovery/Recommendation/interfaces/product';
import { SPONSORED_PRODUCTS_POSITIONS_B } from '@/productDiscovery/SponsoredProducts/constant';
import { SponsoredProductsLocationType } from '@/productDiscovery/SponsoredProducts/interfaces/SponsoredProducts';
import { isSponsoredProduct } from '@/productDiscovery/SponsoredProducts/utils/isSponsoredProduct';
import { sendClickTracking } from '@/productDiscovery/SponsoredProducts/utils/sendClickTracking';
import { sendImpressionsTracking } from '@/productDiscovery/SponsoredProducts/utils/sendImpressionsTracking';
import { PlaylistTitleSponsored } from '@/sellerAdsManagement/SponsoredProducts/components/PlaylistTitleSponsored/PlaylistTitleSponsored';

import { PlaylistFilter } from '../PlaylistFilter/PlaylistFilter';
import { PlaylistTitle } from '../PlaylistTitle/PlaylistTitle';
import {
  allBrandsLabel,
  allCategoriesLabel,
  carouselArrowNext,
  carouselArrowPrev,
} from './translations';

import styles from './Playlist.module.scss';

export interface PlaylistProps {
  className?: string;
  sliderClassName?: string;
  controlsClassName?: string;
  arrowsColorClassName?: string;
  arrowsDisabledColorClassName?: string;
  withProductCardsLight?: boolean;
  playlist: RecommendationPlaylist;
  displayDeliveryLabel?: boolean;
  trackingInfos?: { titleLabel?: string; blockClicked?: string };
  addToCart?: ProductCardProps['addToCart'];
  addToCartButtonVariant?: ProductCardProps['addToCartButtonVariant'];
  position?: number;
  displayAddToCartModal?: boolean;
  displayAddToCartCustomSummary?: boolean;
  autoUpdateAddToCartStateDisabled?: boolean;
  titleLabel?: string;
  legalMention?: boolean;
  isBrandsFilterEnabled?: boolean;
  isCategoriesFilterEnabled?: boolean;
  bannerClassName?: string;
  verticalPlaylist?: boolean;
  withSideArrows?: boolean;
  playlistLocation: string;
}

export const Playlist = ({
  trackingInfos,
  className,
  sliderClassName,
  controlsClassName,
  arrowsColorClassName,
  arrowsDisabledColorClassName,
  withProductCardsLight,
  playlist,
  displayDeliveryLabel,
  addToCart,
  addToCartButtonVariant,
  position,
  displayAddToCartModal,
  displayAddToCartCustomSummary,
  autoUpdateAddToCartStateDisabled,
  titleLabel,
  legalMention,
  isBrandsFilterEnabled,
  isCategoriesFilterEnabled,
  withSideArrows = false,
  playlistLocation,
}: PlaylistProps) => {
  const dispatch = useDispatch();
  const { playlistReconciliation, playlistFingerprint } =
    usePlaylistReconciliation();

  const withFilters = isBrandsFilterEnabled || isCategoriesFilterEnabled;

  const rootClassnames = classnames(styles.root, className, {
    [styles.withFilters]: withFilters,
    [styles.rootWithSideArrows]: isBrandsFilterEnabled && withSideArrows,
  });

  const rootSliderClassnames = classnames(styles.slider, sliderClassName, {
    [styles.sliderB2b]: isB2B(),
  });

  const controlsClassnames = classnames(styles.controls, controlsClassName);
  useDynamicReducer('productDiscovery.fingerprint', fingerprintReducer);

  const { onDisplay, ref, onClick } = useRecommendationPlaylistTracking({
    engineName: playlist.engineName || '',
    playlistLocation,
    playlistName:
      trackingInfos?.blockClicked ||
      trackingInfos?.titleLabel ||
      playlist.title,
  });

  const sendViewTracking = (
    product: RecoProductWithSponsoredData,
    index: number,
  ) => {
    if (
      isSponsoredProduct(product) &&
      playlistLocation &&
      ['A2C_Popin', 'cart_page'].includes(playlistLocation)
    ) {
      sendImpressionsTracking(
        [
          {
            product: {
              adTrackingData: product.adTrackingData!,
              cpc: product.cpc!,
              productId: Number(product.productId),
              sellerId: product.sellerId,
            },
            position: product.position!,
          },
        ],
        playlistLocation === 'A2C_Popin'
          ? SponsoredProductsLocationType.ADD_TO_CART_PAGE
          : SponsoredProductsLocationType.CART_PAGE,
        0,
      );
    }

    onDisplay(index, product);
  };

  const sendTracking = async (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    product: RecoProductWithSponsoredData,
    cardPosition: number,
    defaultTracking: ProductCardClickTracking,
  ) => {
    if (
      isSponsoredProduct(product) &&
      playlistLocation &&
      ['A2C_Popin', 'cart_page'].includes(playlistLocation)
    ) {
      const {
        adTrackingData,
        cpc,
        position: productPosition,
        ...productWithoutSponsoredData
      } = product;
      sendClickTracking(
        productWithoutSponsoredData,
        playlistLocation === 'A2C_Popin'
          ? SponsoredProductsLocationType.ADD_TO_CART_PAGE
          : SponsoredProductsLocationType.CART_PAGE,
        adTrackingData!,
        cpc!,
        productPosition!,
        0,
      );
    }

    onClick(cardPosition, product);
  };

  const onAddToCart = async (payload: AddToCartPayload) => {
    if (
      playlistLocation &&
      ['A2C_Popin', 'cart_page'].includes(playlistLocation)
    ) {
      const product: RecoProductWithSponsoredData | undefined =
        playlist.products.find(
          (p) => p.modelId === payload.products[0].modelId,
        );

      if (product && isSponsoredProduct(product)) {
        const {
          adTrackingData,
          cpc,
          position: productPosition,
          ...productWithoutSponsoredData
        } = product;
        sendClickTracking(
          productWithoutSponsoredData,
          playlistLocation === 'A2C_Popin'
            ? SponsoredProductsLocationType.ADD_TO_CART_PAGE
            : SponsoredProductsLocationType.CART_PAGE,
          adTrackingData!,
          cpc!,
          productPosition!,
          0,
        );
      }
    }

    const payloadWithReconciliation = { ...payload };
    payloadWithReconciliation.playlistReconciliation = {
      playlistFingerprint,
      playlistReconciliation,
      recoEngineName: playlist.engineName,
      blockClicked: playlist.title,
      playlistName: trackingInfos,
      playlistPosition: position || 0,
    };
    const { addToCart: addToCartAction } = await import(
      '@/domains/shoppingCart/AddToCart/AddToCart.actions'
    );
    dispatch(addToCartAction(payloadWithReconciliation));
  };

  const { filteredProductsByBrand, selectedBrand, selectBrand, brands } =
    useBrandsFilter({
      products: playlist.products,
      playlistName: playlist.engineName,
    });

  const {
    filteredProductsByCategory,
    selectedCategory,
    selectCategory,
    categories,
  } = useCategoriesFilter({
    products: playlist.products,
    playlistName: playlist.engineName,
  });

  const playlistFullySponsored =
    playlist.numberSPDisplay &&
    playlist.numberSPDisplay > SPONSORED_PRODUCTS_POSITIONS_B.length;

  const getMerchandising = (product: RecoProductWithSponsoredData) => {
    const promoTags =
      getMerchandisingValue({
        product,
      }) || [];

    const merchandising: Merchandising[] =
      product.isSponsored && !playlistFullySponsored
        ? ['sponsored']
        : promoTags;
    return merchandising;
  };

  const TitleAndFilters = () => {
    return (
      <>
        {playlistFullySponsored ? (
          <PlaylistTitleSponsored
            text={titleLabel ?? playlist.title}
            playlist={playlist}
          />
        ) : (
          <PlaylistTitle text={titleLabel ?? playlist.title} />
        )}

        {withFilters && (
          <div className={styles.filter}>
            {isBrandsFilterEnabled && (
              <PlaylistFilter
                items={brands.map((brand) => ({
                  itemName: brand.brandName,
                  itemId: brand.brandId,
                }))}
                selectItem={selectBrand}
                selectedItem={selectedBrand}
                allItemsLabel={allBrandsLabel}
              />
            )}
            {isCategoriesFilterEnabled && (
              <PlaylistFilter
                items={categories.map((category) => ({
                  itemName: category.categoryName,
                  itemId: category.categoryId,
                }))}
                selectItem={selectCategory}
                selectedItem={selectedCategory}
                allItemsLabel={allCategoriesLabel}
              />
            )}
          </div>
        )}
      </>
    );
  };

  let productsToDisplay = playlist.products;
  if (isBrandsFilterEnabled) productsToDisplay = filteredProductsByBrand;
  if (isCategoriesFilterEnabled) productsToDisplay = filteredProductsByCategory;

  if (!playlist.products.length) {
    return null;
  }

  const scrollBy = getScrollByFromWidth({
    itemWidth: 226,
    maxScrollBy: playlistLocation === 'cart_page' ? 4 : 6,
    maxPlaylistWidth: playlistLocation === 'cart_page' ? 864 : undefined,
  });

  const trackArrowClick = (direction: Direction) => {
    Gtm.push({
      event: 'interaction_click on navigate playlist',
      e_category: 'navigation',
      e_action: 'click on navigate playlist',
      e_label: playlist.engineName
        ? `${playlistLocation}-playlist-${playlist.engineName}`
        : playlistLocation,
      amp_event_name: 'Click on navigate playlist',
      playlist_name: playlist.engineName
        ? `${playlistLocation}-playlist-${playlist.engineName}`
        : playlistLocation,
      event_trigger: `${direction}`,
    });
  };

  return (
    <Carousel
      ref={ref}
      key={playlist.id}
      title={<TitleAndFilters />}
      className={rootClassnames}
      controlsClassName={controlsClassnames}
      arrowsColorClassName={arrowsColorClassName}
      arrowsDisabledColorClassName={arrowsDisabledColorClassName}
      sliderClassName={rootSliderClassnames}
      ariaLabelLeft={carouselArrowPrev}
      ariaLabelRight={carouselArrowNext}
      dataTestId={
        playlist.engineName
          ? `${playlistLocation}-playlist-${playlist.engineName}`
          : playlistLocation
      }
      trackArrowClick={trackArrowClick}
      withSideArrows={withSideArrows}
      sliderDirectionOptions={{ scrollBy }}
    >
      {productsToDisplay.map((item, index) => (
        <AlgoliaProductCard
          key={item.productId || position}
          product={item}
          b2b={isB2B()}
          position={index}
          vertical
          light={withProductCardsLight}
          displayDeliveryLabel={displayDeliveryLabel}
          onClick={sendTracking}
          onViewed={() => sendViewTracking(item, index)}
          addToCart={addToCart}
          addToCartButtonVariant={addToCartButtonVariant}
          displayAddToCartModal={displayAddToCartModal}
          displayAddToCartCustomSummary={displayAddToCartCustomSummary}
          autoUpdateAddToCartStateDisabled={autoUpdateAddToCartStateDisabled}
          onAddToCart={onAddToCart}
          legalMention={legalMention}
          imageLoading="lazy"
          merchandising={getMerchandising(item)}
          adPayer={item.adPayer}
          productIsSponsored={item.isSponsored}
          className={styles.playlistProductCard}
        />
      ))}
    </Carousel>
  );
};
